import { itemTypeConfigMap, normalizeKeyForColorContrastCrossIssue } from '@wix/a11y-wizard-common';
import { useTranslation } from '@wix/fe-essentials/react';
import { RuleListStatus, useOnRuleGroupToggle } from '@wix/rules-panel-ui';
import { assertNotOptional } from '@wix/seo-common-core';
import { useAtom, useAtomValue, useSetAtom, useStore } from 'jotai';
import { groupBy } from 'lodash';
import { useCallback, useMemo, useRef } from 'react';
import { cancelHeadingStructureAIAtom, headingStructureAILoadingPageIdAtom } from '../../atoms/heading-structure-ai-atoms';
import { crossPageColorContrastIssuesAtom, rescanTriggerGroupIdAtom, unsavedChangedHandlersAtom, verticalsDrillInPageIdAtom } from '../../atoms/session-atoms';
import { BiCtaNames } from '../../bi/types';
import { usePanelStateAtom } from '../../hooks/rules-state';
import { useA11yWizardAdapter } from '../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../hooks/use-bi-logger';
import { usePanelCloseHandler } from '../../hooks/use-panel-close-handler';
import { showSaveBeforeCloseModal } from '../modals/show-save-before-leave-modal';
import { showStopAIHeadingStructureModal } from '../modals/show-stop-ai-heading-structure-modal';
import { useBuildCrossPagesRuleResult } from '../verticals-drill-in-utils/use-build-cross-pages-rule-result';
import { useBuildSpecificPageIssueResults } from '../verticals-drill-in-utils/use-build-specific-page-issue-results';
import { useBuildSpecificPageRuleGroups } from '../verticals-drill-in-utils/use-build-specific-page-rule-groups';
import { getGroupedExecutionResults, getIsAllCrossPageIssuePassed } from '../verticals-drill-in-utils/use-get-entity-summary-after-cross-page-calculation';
import { useSpecificRuleGroupPagination } from './use-specific-rule-group-pagination';
export const useVerticalDrillInScreen = () => {
  var _crossPagesIssues$cro;
  const [verticalsDrillInPageId, setVerticalsDrillInPageId] = useAtom(verticalsDrillInPageIdAtom);
  const {
    t
  } = useTranslation();
  const biLogger = useBiLogger();
  const crossPagesRuleResultBuilder = useBuildCrossPagesRuleResult();
  const specificPageIssueResultsBuilder = useBuildSpecificPageIssueResults();
  assertNotOptional(verticalsDrillInPageId, 'verticalsDrillInPageId');
  const setRescanTriggerGroupId = useSetAtom(rescanTriggerGroupIdAtom);
  const {
    a11yWizardAdapter,
    editorHost
  } = useA11yWizardAdapter();
  const unsavedChangedHandlersValue = useAtomValue(unsavedChangedHandlersAtom);
  const setCrossPageColorContrastIssuesInSession = useSetAtom(crossPageColorContrastIssuesAtom);
  const {
    getPagesData,
    panelState
  } = usePanelStateAtom();
  const divRef = useRef(null);
  const pagesData = getPagesData();
  const jotaiStore = useStore();
  const aiLoadingPageId = useAtomValue(headingStructureAILoadingPageIdAtom);
  const cancelHeadingStructureAI = useSetAtom(cancelHeadingStructureAIAtom);
  const navigateBackToMainPanel = useCallback(() => {
    const navigateBack = () => {
      setRescanTriggerGroupId(undefined);
      setVerticalsDrillInPageId(undefined);
    };
    if (aiLoadingPageId) {
      showStopAIHeadingStructureModal({
        a11yWizardAdapter,
        editorHost,
        handleContinue: () => {},
        handleStop: () => {
          cancelHeadingStructureAI(aiLoadingPageId);
          navigateBack();
        },
        t
      });
    } else if (unsavedChangedHandlersValue != null && unsavedChangedHandlersValue.exists) {
      showSaveBeforeCloseModal({
        a11yWizardAdapter,
        biLogger,
        editorHost,
        handleApply: unsavedChangedHandlersValue.apply,
        handleDiscard: navigateBack,
        t
      });
    } else {
      navigateBack();
    }
  }, [aiLoadingPageId, a11yWizardAdapter, biLogger, cancelHeadingStructureAI, editorHost, setRescanTriggerGroupId, setVerticalsDrillInPageId, t, unsavedChangedHandlersValue]);
  const closePanel = useCallback(() => {
    biLogger.ctaClick({
      ctaName: BiCtaNames.CloseTheWizard
    });
    if (aiLoadingPageId) {
      showStopAIHeadingStructureModal({
        a11yWizardAdapter,
        editorHost,
        handleContinue: () => {},
        handleStop: () => {
          cancelHeadingStructureAI(aiLoadingPageId);
          a11yWizardAdapter.togglePanel();
        },
        t
      });
    } else if (unsavedChangedHandlersValue != null && unsavedChangedHandlersValue.exists) {
      showSaveBeforeCloseModal({
        a11yWizardAdapter,
        biLogger,
        editorHost,
        handleApply: unsavedChangedHandlersValue.apply,
        handleDiscard: () => a11yWizardAdapter.togglePanel(),
        t
      });
    } else {
      a11yWizardAdapter.togglePanel();
    }
  }, [a11yWizardAdapter, aiLoadingPageId, biLogger, cancelHeadingStructureAI, editorHost, t, unsavedChangedHandlersValue]);
  const itemType = useMemo(() => {
    var _pagesData$get;
    return (_pagesData$get = pagesData.get(verticalsDrillInPageId)) == null ? void 0 : _pagesData$get.itemType;
  }, [pagesData, verticalsDrillInPageId]);
  assertNotOptional(itemType, 'itemType');
  const screenTitle = useMemo(() => {
    var _pagesData$get2;
    return ((_pagesData$get2 = pagesData.get(verticalsDrillInPageId)) == null ? void 0 : _pagesData$get2.title) ?? '';
  }, [pagesData, verticalsDrillInPageId]);
  const pageLimitByItemType = useMemo(() => {
    var _itemTypeConfigMap;
    return (_itemTypeConfigMap = itemTypeConfigMap[itemType]) == null ? void 0 : _itemTypeConfigMap.pageLimit;
  }, [itemType]);
  const limitedRoutesByItemId = useMemo(() => {
    var _pageData$innerRoutes;
    const pageData = pagesData.get(verticalsDrillInPageId);
    if (!(pageData != null && (_pageData$innerRoutes = pageData.innerRoutes) != null && _pageData$innerRoutes.length)) {
      return {};
    }
    const limitedRoutes = pageData.innerRoutes.slice(0, pageLimitByItemType);
    return groupBy(limitedRoutes, router => router.id);
  }, [pagesData, verticalsDrillInPageId, pageLimitByItemType]);
  const executionResults = useMemo(() => {
    const allResults = Array.from(panelState.groupsDataMap.values()).filter(group => group.itemType === itemType).flatMap(group => group.ruleExecutionResults || []);
    return getGroupedExecutionResults(allResults);
  }, [panelState.groupsDataMap, itemType]);
  const [unsavedChangedHandlers, setUnsavedChangedHandlers] = useAtom(unsavedChangedHandlersAtom);
  const unsavedChangedHandlersProps = useMemo(() => ({
    navigationGuard: aiLoadingPageId ? {
      exists: true,
      onNavigationAttempt: proceed => showStopAIHeadingStructureModal({
        a11yWizardAdapter,
        editorHost,
        handleContinue: () => {},
        handleStop: () => {
          cancelHeadingStructureAI(aiLoadingPageId);
          proceed();
        },
        t
      })
    } : undefined,
    setUnsavedChanges: (exists, apply) => setUnsavedChangedHandlers({
      apply,
      exists
    }),
    showSaveBeforeLeaveModal: (handleApply, handleDiscard) => showSaveBeforeCloseModal({
      a11yWizardAdapter,
      biLogger,
      editorHost,
      handleApply,
      handleDiscard,
      t
    }),
    unsavedChanges: unsavedChangedHandlers ? unsavedChangedHandlers : {
      apply: () => Promise.resolve(),
      exists: false
    }
  }), [aiLoadingPageId, setUnsavedChangedHandlers, a11yWizardAdapter, biLogger, cancelHeadingStructureAI, editorHost, t, unsavedChangedHandlers]);
  const toggleFunctions = useOnRuleGroupToggle({
    unsavedChangesHandlers: unsavedChangedHandlersProps
  });
  const handleCloseAttempt = useCallback(() => {
    biLogger.ctaClick({
      ctaName: BiCtaNames.CloseTheWizard
    });
  }, [biLogger]);
  const handleCloseWithModal = useCallback(closePanelDirectly => {
    if (aiLoadingPageId) {
      showStopAIHeadingStructureModal({
        a11yWizardAdapter,
        editorHost,
        handleContinue: () => {},
        handleStop: () => {
          cancelHeadingStructureAI(aiLoadingPageId);
          closePanelDirectly();
        },
        t
      });
      return true;
    }
    if (unsavedChangedHandlersValue != null && unsavedChangedHandlersValue.exists) {
      showSaveBeforeCloseModal({
        a11yWizardAdapter,
        biLogger,
        editorHost,
        handleApply: unsavedChangedHandlersValue.apply,
        handleDiscard: closePanelDirectly,
        t
      });
      return true;
    }
    return false;
  }, [aiLoadingPageId, unsavedChangedHandlersValue, a11yWizardAdapter, biLogger, cancelHeadingStructureAI, editorHost, t]);
  usePanelCloseHandler({
    handleCloseWithModal,
    onCloseAttemptBI: handleCloseAttempt
  });
  const {
    crossPagesRuleResult,
    crossPagesIssues
  } = crossPagesRuleResultBuilder({
    executionResults,
    pageId: verticalsDrillInPageId
  });
  const desktopIssues = (_crossPagesIssues$cro = crossPagesIssues.crossPagesColorContrastIssues) == null ? void 0 : _crossPagesIssues$cro.DESKTOP;
  if (desktopIssues != null && desktopIssues.result.failedElements.length) {
    setCrossPageColorContrastIssuesInSession(new Set([...(desktopIssues.result.failedElements.map(el => normalizeKeyForColorContrastCrossIssue(el)) || [])]));
  }
  const specificPageIssueResults = specificPageIssueResultsBuilder({
    crossPageIssues: crossPagesIssues,
    executionResults
  });
  const isAllCrossPageIssuePassed = useMemo(() => {
    return getIsAllCrossPageIssuePassed(crossPagesRuleResult);
  }, [crossPagesRuleResult]);
  const specificPageRuleGroups = useBuildSpecificPageRuleGroups({
    isAllCrossPageIssuePassed,
    itemType,
    ruleResults: specificPageIssueResults,
    tpaPageId: verticalsDrillInPageId
  });
  const getFilteredRuleGroups = useCallback((ruleGroups, limitedRoutes) => {
    if (!ruleGroups.length || !limitedRoutes) {
      return [];
    }
    return ruleGroups.filter(ruleGroup => {
      const hasLimitedRoute = ruleGroup.groupId in limitedRoutes;
      return hasLimitedRoute && limitedRoutes[ruleGroup.groupId];
    });
  }, []);
  const limitedRuleGroups = useMemo(() => getFilteredRuleGroups(specificPageRuleGroups, limitedRoutesByItemId), [specificPageRuleGroups, limitedRoutesByItemId, getFilteredRuleGroups]);
  const specificRuleGroupPaginationObject = useSpecificRuleGroupPagination({
    ruleGroups: limitedRuleGroups
  });
  const shouldShowMaxPagesWarning = useMemo(() => {
    const {
      inNextButtonDisabled
    } = specificRuleGroupPaginationObject;
    if (!pageLimitByItemType) {
      return false;
    }
    const totalResults = Object.keys(executionResults).length;
    return totalResults > pageLimitByItemType && inNextButtonDisabled;
  }, [executionResults, pageLimitByItemType, specificRuleGroupPaginationObject]);
  const totalNumOfIssues = useMemo(() => {
    const crossPageIssueNumber = crossPagesRuleResult.filter(result => (result == null ? void 0 : result.status) === RuleListStatus.failed).length;
    const specificPageIssueNumber = specificPageRuleGroups.reduce((acc, group) => {
      const groupIssueNum = group.ruleResults.filter(result => (result == null ? void 0 : result.status) === RuleListStatus.failed).length;
      return acc + groupIssueNum;
    }, 0);
    return crossPageIssueNumber + specificPageIssueNumber;
  }, [crossPagesRuleResult, specificPageRuleGroups]);
  return useMemo(() => {
    return {
      closePanel,
      crossPagesRuleResult,
      divRef,
      executionResults,
      isAllCrossPageIssuePassed,
      itemType,
      jotaiStore,
      limitedRuleGroups,
      navigateBackToMainPanel,
      pageId: verticalsDrillInPageId,
      screenTitle,
      shouldShowMaxPagesWarning,
      specificRuleGroupPaginationObject,
      toggleFunctions,
      totalNumOfIssues,
      unsavedChangedHandlersProps
    };
  }, [closePanel, crossPagesRuleResult, executionResults, itemType, jotaiStore, navigateBackToMainPanel, verticalsDrillInPageId, screenTitle, shouldShowMaxPagesWarning, specificRuleGroupPaginationObject, toggleFunctions, totalNumOfIssues, unsavedChangedHandlersProps, isAllCrossPageIssuePassed, limitedRuleGroups]);
};
//# sourceMappingURL=use-vertical-drill-in-screen.js.map